/**
 * Digital release pack — what a dispatcher hands the PIC before departure.
 * Derived from the existing Mission ticket. Never invents OFP / W&B / wx / flight#.
 */

import { coerceClockDate, formatCardDateInZone, formatHmInZone, formatUtcCardDate, formatUtcHm, HALIFAX_TZ } from "./clock";
import { displayFlightNumber, missionTicketRef } from "./flightNumber";
import { hasGroundingMel, melBriefForTail, openMelFor } from "./mel";
import { notamChipLabel, notamsForIcaos, type NotamNotice } from "./notam";
import { fuelKindForType } from "./catalogs";
import { ofpBlockFuelLb, ofpTripFuelLb } from "./fuelBurn";
import { convertFuel, formatFuelFigure } from "./fuelUnits";
import { safeTrim } from "./text";
import { TRIP_SUPPORT_SERVICE_LABEL, tripSupportOf } from "./tripSupport";
import type { MelItem, Mission, MissionReleasePack, ReleasePackStatus } from "./types";
import { hasFieldWx, wxChipLabel, type FieldWx } from "./weather";

export const RELEASE_PACK_STATUSES: readonly ReleasePackStatus[] = ["draft", "ready", "released"];

export const RELEASE_PACK_STATUS_LABEL: Record<ReleasePackStatus, string> = {
  draft: "Draft",
  ready: "Ready to release",
  released: "Released",
};

/** Compact board / Gantt chip. Draft is the affordance, not a second status language. */
export const RELEASE_PACK_CHIP_LABEL: Record<ReleasePackStatus, string> = {
  draft: "Release",
  ready: "Ready",
  released: "Released",
};

export type ReleasePackItemId = "identity" | "ofp" | "support" | "weather" | "performance" | "airworthiness";

export type ReleasePackItemState = "present" | "missing" | "partial";

export const RELEASE_PACK_ITEM_LABEL: Record<ReleasePackItemId, string> = {
  identity: "Flight identity",
  ofp: "OFP / SimBrief",
  support: "Trip Support",
  weather: "Weather / NOTAM",
  performance: "Payload / fuel / W&B",
  airworthiness: "Airworthiness / MEL",
};

export type ReleasePackItem = {
  id: ReleasePackItemId;
  label: string;
  state: ReleasePackItemState;
  summary: string;
};

export type ReleasePackHints = {
  picName?: string;
  aircraftType?: string;
  timeZone?: string;
  originWx?: FieldWx;
  destWx?: FieldWx;
  notams?: NotamNotice[];
  notamNote?: string;
  notamAvailable?: boolean;
  melItems?: MelItem[];
};

export type ReleasePackView = {
  missionId: string;
  flightNumber: string;
  status: ReleasePackStatus;
  statusLabel: string;
  updatedAtUtc?: string;
  releasedAtUtc?: string;
  items: ReleasePackItem[];
};

const STATUS_SET: ReadonlySet<string> = new Set(RELEASE_PACK_STATUSES);

export function isReleasePackStatus(value: unknown): value is ReleasePackStatus {
  return typeof value === "string" && STATUS_SET.has(value);
}

export function hydrateReleasePack(raw: unknown): MissionReleasePack {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "draft" };
  }
  const row = raw as Partial<MissionReleasePack>;
  const status = isReleasePackStatus(row.status) ? row.status : "draft";
  const updatedAtUtc = safeTrim(row.updatedAtUtc) || undefined;
  const releasedAtUtc = status === "released" ? safeTrim(row.releasedAtUtc) || undefined : undefined;
  return {
    status,
    ...(updatedAtUtc ? { updatedAtUtc } : {}),
    ...(releasedAtUtc ? { releasedAtUtc } : {}),
  };
}

export function releasePackOf(mission: Pick<Mission, "releasePack"> | null | undefined): MissionReleasePack {
  return hydrateReleasePack(mission?.releasePack);
}

export function releasePackStatusOf(mission: Pick<Mission, "releasePack"> | null | undefined): ReleasePackStatus {
  return releasePackOf(mission).status;
}

export function releasePackChipLabel(status: ReleasePackStatus): string {
  return RELEASE_PACK_CHIP_LABEL[status];
}

export function releasePackTicketRef(mission: Pick<Mission, "id" | "flightNumber" | "callsign">): {
  missionId: string;
  flightNumber: string;
} {
  return missionTicketRef(mission);
}

/** Critical Missing items that must not flip Ready / Released in one click. Support is optional. */
export const RELEASE_PACK_CRITICAL_ITEMS: readonly ReleasePackItemId[] = [
  "identity",
  "ofp",
  "performance",
  "airworthiness",
];

export type ReleasePackReadiness = {
  blockers: ReleasePackItem[];
  warnings: ReleasePackItem[];
  canMarkReady: boolean;
};

export type ReleasePackReadinessPrefs = {
  releaseSoftBlockReady?: boolean;
  releaseCriticalItems?: readonly ReleasePackItemId[];
};

export function releasePackReadiness(
  view: ReleasePackView,
  prefs?: ReleasePackReadinessPrefs | null,
): ReleasePackReadiness {
  const softOn = prefs ? Boolean(prefs.releaseSoftBlockReady) : true;
  const critical = new Set<ReleasePackItemId>(
    prefs?.releaseCriticalItems?.length ? prefs.releaseCriticalItems : RELEASE_PACK_CRITICAL_ITEMS,
  );
  const blockers = softOn
    ? view.items.filter((row) => critical.has(row.id) && row.state === "missing")
    : [];
  const warnings = view.items.filter((row) => {
    if (blockers.some((hit) => hit.id === row.id)) return false;
    if (row.state === "partial") return true;
    if (row.id === "weather" && row.state === "missing") return true;
    return false;
  });
  return { blockers, warnings, canMarkReady: blockers.length === 0 };
}

export function releasePackNeedsOverride(
  status: ReleasePackStatus,
  readiness: ReleasePackReadiness,
): boolean {
  return (status === "ready" || status === "released") && !readiness.canMarkReady;
}

export function releasePackGateCopy(readiness: ReleasePackReadiness): string {
  const missing = readiness.blockers.map((row) => row.label);
  const warn = readiness.warnings.map((row) => `${row.label} (${row.state})`);
  const bits = [
    missing.length ? `Missing: ${missing.join(", ")}` : "",
    warn.length ? `Partial: ${warn.join(", ")}` : "",
  ].filter(Boolean);
  if (!bits.length) return "";
  return `${bits.join(" · ")}. Ready / Released needs an override — nothing invented.`;
}

export function releasePackSearchHay(mission: Pick<Mission, "releasePack">): string {
  const pack = releasePackOf(mission);
  return [pack.status, RELEASE_PACK_STATUS_LABEL[pack.status], RELEASE_PACK_CHIP_LABEL[pack.status]].join(" ");
}

function esc(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function item(id: ReleasePackItemId, state: ReleasePackItemState, summary: string): ReleasePackItem {
  return { id, label: RELEASE_PACK_ITEM_LABEL[id], state, summary };
}

export function formatReleasePackLocalAndZulu(iso: string | undefined, timeZone = HALIFAX_TZ): string {
  const date = coerceClockDate(iso);
  if (!date) return "—";
  const zone = safeTrim(timeZone) || HALIFAX_TZ;
  return `${formatCardDateInZone(date, zone)} ${formatHmInZone(date, zone)} · ${formatUtcCardDate(date)} ${formatUtcHm(date)}`;
}

function staIso(mission: Pick<Mission, "departureIso" | "eteMin">): string | undefined {
  const start = coerceClockDate(mission.departureIso);
  if (!start) return undefined;
  const ete = typeof mission.eteMin === "number" && Number.isFinite(mission.eteMin) ? mission.eteMin : 0;
  return new Date(start.getTime() + ete * 60_000).toISOString();
}

function identityItem(mission: Mission, hints: ReleasePackHints): ReleasePackItem {
  const flight = displayFlightNumber(mission) || safeTrim(mission.callsign);
  const origin = safeTrim(mission.originIcao).toUpperCase();
  const dest = safeTrim(mission.destIcao).toUpperCase();
  const reg = safeTrim(mission.registration);
  const type = safeTrim(hints.aircraftType) || safeTrim(mission.aircraftName);
  const std = formatReleasePackLocalAndZulu(mission.departureIso, hints.timeZone);
  const sta = formatReleasePackLocalAndZulu(staIso(mission), hints.timeZone);
  const pic = safeTrim(hints.picName);
  const bits = [
    flight || "Flight # missing",
    origin && dest ? `${origin}→${dest}` : "Route missing",
    `STD ${std}`,
    `STA ${sta}`,
    [reg || "Reg missing", type].filter(Boolean).join(" "),
    pic ? `PIC ${pic}` : "PIC unassigned",
  ];
  const haveId = Boolean(flight);
  const haveRoute = Boolean(origin && dest);
  const haveStd = std !== "—";
  const haveReg = Boolean(reg);
  if (haveId && haveRoute && haveStd && haveReg) {
    return item("identity", "present", bits.join(" · "));
  }
  if (haveId || haveRoute || haveStd || haveReg) {
    return item("identity", "partial", bits.join(" · "));
  }
  return item("identity", "missing", "Flight identity is not on this ticket. Nothing invented.");
}

function ofpItem(mission: Mission): ReleasePackItem {
  const ofp = mission.ofpAttachment;
  if (!ofp) {
    return item("ofp", "missing", "OFP / SimBrief is not attached to this ticket.");
  }
  const bits = [
    ofp.callsign || ofp.originIcao && ofp.destIcao ? `${ofp.originIcao}→${ofp.destIcao}` : "",
    ofp.route ? `Route ${ofp.route}` : "",
    ofp.aircraftType || "",
    ofp.retrievedAtUtc ? `Retrieved ${ofp.retrievedAtUtc}` : "",
    ofp.pdfUrl ? "PDF on file" : ofp.ofpXmlUrl ? "XML on file" : "",
  ].filter(Boolean);
  return item("ofp", "present", bits.join(" · ") || `OFP on file (${ofp.matchNote || ofp.match || "attached"})`);
}

function supportItem(mission: Mission): ReleasePackItem {
  const lines = tripSupportOf(mission).filter((line) => line.status !== "cancelled");
  if (!lines.length) {
    return item("support", "missing", "No trip-support lines on this ticket.");
  }
  const bits = lines.map((line) => {
    const where = line.airportIcao || "—";
    const vendor = safeTrim(line.vendorName);
    return `${TRIP_SUPPORT_SERVICE_LABEL[line.serviceType]} ${where}${vendor ? ` · ${vendor}` : ""} · ${line.status}`;
  });
  const requested = lines.filter((line) => line.status === "requested").length;
  const state: ReleasePackItemState = requested ? "partial" : "present";
  return item("support", state, bits.join(" · "));
}

function weatherItem(mission: Mission, hints: ReleasePackHints): ReleasePackItem {
  const note = safeTrim(mission.weatherNote);
  const originMetar = safeTrim(mission.originMetar);
  const destMetar = safeTrim(mission.destMetar);
  const originWx = hints.originWx && hasFieldWx(hints.originWx) ? wxChipLabel(hints.originWx, "DEP") : "";
  const destWx = hints.destWx && hasFieldWx(hints.destWx) ? wxChipLabel(hints.destWx, "ARR") : "";
  const notamAvailable = hints.notamAvailable !== false;
  const notams = notamAvailable ? notamsForIcaos(hints.notams ?? [], [mission.originIcao, mission.destIcao]) : [];
  const notamBits = notams.slice(0, 4).map((n) => notamChipLabel(n));
  const bits = [
    note,
    originMetar ? `DEP METAR ${originMetar}` : "",
    destMetar ? `ARR METAR ${destMetar}` : "",
    originWx,
    destWx,
    notamBits.length ? notamBits.join(" · ") : "",
    notams.length > 4 ? `+${notams.length - 4} NOTAM` : "",
  ].filter(Boolean);
  if (bits.length) {
    const haveWx = Boolean(note || originMetar || destMetar || originWx || destWx);
    const haveNotam = notamBits.length > 0;
    return item("weather", haveWx && haveNotam ? "present" : "partial", bits.join(" · "));
  }
  if (hints.notamAvailable === false) {
    return item("weather", "missing", hints.notamNote || "Weather / NOTAM not loaded. Nothing invented.");
  }
  return item("weather", "missing", hints.notamNote || "No weather or flight-affecting NOTAMs on this ticket.");
}

function performanceItem(mission: Mission): ReleasePackItem {
  const fuelGal = typeof mission.fuelGal === "number" && Number.isFinite(mission.fuelGal) ? mission.fuelGal : 0;
  const fuelLbs = typeof mission.fuelLbs === "number" && Number.isFinite(mission.fuelLbs) ? mission.fuelLbs : 0;
  const kind = fuelKindForType(mission.ofpAttachment?.aircraftType);
  const tripLb = ofpTripFuelLb(mission.ofpAttachment, mission.ofpAttachment?.aircraftType);
  const blockLb = ofpBlockFuelLb(mission.ofpAttachment, mission.ofpAttachment?.aircraftType);
  const cargo = typeof mission.cargoLb === "number" && Number.isFinite(mission.cargoLb) ? mission.cargoLb : undefined;
  const pax = typeof mission.paxCount === "number" && Number.isFinite(mission.paxCount) ? mission.paxCount : 0;
  const bits: string[] = [];
  if (fuelLbs > 0) bits.push(`Ticket fuel ${formatFuelFigure(fuelLbs, "lb")}`);
  else if (fuelGal > 0) bits.push(`Ticket fuel ${formatFuelFigure(convertFuel(fuelGal, "gal", "lb", kind), "lb")}`);
  if (tripLb != null && tripLb > 0) bits.push(`OFP trip ${formatFuelFigure(tripLb, "lb")}`);
  if (blockLb != null && blockLb > 0) bits.push(`OFP block ${formatFuelFigure(blockLb, "lb")}`);
  if (pax > 0) bits.push(`PAX ${pax}`);
  if (cargo != null) bits.push(`Cargo ${cargo} lb`);
  bits.push("W&B sheet is not stored on this ticket");
  if (fuelGal > 0 || fuelLbs > 0 || (tripLb != null && tripLb > 0) || (blockLb != null && blockLb > 0) || pax > 0 || cargo != null) {
    return item("performance", "present", bits.join(" · "));
  }
  return item("performance", "missing", "No planned fuel or payload on this ticket. W&B numbers are not invented.");
}

function airworthinessItem(mission: Mission, hints: ReleasePackHints): ReleasePackItem {
  if (!hints.melItems) {
    return item("airworthiness", "missing", "MEL list is not on this desk. Nothing invented.");
  }
  const tail = safeTrim(mission.aircraftId);
  if (!tail) {
    return item("airworthiness", "missing", "No tail on this ticket — MEL cannot be matched.");
  }
  const open = openMelFor(tail, hints.melItems);
  const grounded = hasGroundingMel(tail, hints.melItems);
  const brief = melBriefForTail(hints.melItems, tail);
  if (grounded) return item("airworthiness", "partial", `Grounding MEL · ${brief}`);
  if (open.length) return item("airworthiness", "partial", brief);
  return item("airworthiness", "present", brief);
}

export type ChecklistTipLine = {
  id: ReleasePackItemId;
  label: string;
  state: "done" | "missing" | "partial";
};

/** Bar tooltip rows. Same items as `buildReleasePack`, with present shown as done. */
export function checklistTipLines(mission: Mission, hints: ReleasePackHints = {}): ChecklistTipLine[] {
  return buildReleasePack(mission, hints).items.map((item) => ({
    id: item.id,
    label: item.label,
    state: item.state === "present" ? "done" : item.state,
  }));
}

export function buildReleasePack(mission: Mission, hints: ReleasePackHints = {}): ReleasePackView {
  const pack = releasePackOf(mission);
  const ref = releasePackTicketRef(mission);
  return {
    missionId: ref.missionId,
    flightNumber: ref.flightNumber,
    status: pack.status,
    statusLabel: RELEASE_PACK_STATUS_LABEL[pack.status],
    updatedAtUtc: pack.updatedAtUtc,
    releasedAtUtc: pack.releasedAtUtc,
    items: [
      identityItem(mission, hints),
      ofpItem(mission),
      supportItem(mission),
      weatherItem(mission, hints),
      performanceItem(mission),
      airworthinessItem(mission, hints),
    ],
  };
}

export function releasePackPrintBodyHtml(view: ReleasePackView): string {
  const rows = view.items
    .map((row) => {
      const state =
        row.state === "present" ? "On file" : row.state === "partial" ? "Partial" : "Missing";
      return `<h2>${esc(row.label)} · ${esc(state)}</h2><p>${esc(row.summary)}</p>`;
    })
    .join("");
  const stamp = view.releasedAtUtc
    ? `<p class="kicker">Released ${esc(view.releasedAtUtc)}</p>`
    : view.updatedAtUtc
      ? `<p class="kicker">Updated ${esc(view.updatedAtUtc)}</p>`
      : "";
  return [
    `<p class="kicker">Release pack</p>`,
    `<h1>${esc(view.flightNumber || view.missionId)}</h1>`,
    `<p><strong>${esc(view.statusLabel)}</strong> · ticket ${esc(view.missionId)}</p>`,
    stamp,
    rows,
  ].join("");
}
